import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

export type AuditAction = 
  | 'creation' 
  | 'modification' 
  | 'suppression' 
  | 'activation' 
  | 'desactivation' 
  | 'connexion' 
  | 'deconnexion'
  | 'deconnexion_automatique'
  | 'consultation' 
  | 'telechargement' 
  | 'export' 
  | 'relance' 
  | 'cloture' 
  | 'soumission' 
  | 'assignation' 
  | 'partage' 
  | 'revocation'
  | 'revocation_sessions'
  | 'changement_mot_de_passe'
  | 'reinitialisation_mot_de_passe';

interface AuditLogEntry {
  action: AuditAction;
  table_name: string;
  record_id?: string;
  details?: Record<string, any>;
  user_id?: string;
}

export const auditLogger = {
  async log(entry: AuditLogEntry): Promise<void> {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = entry.user_id || sessionData?.session?.user?.id || null;

      await supabase.from('audit_logs').insert({
        user_id: userId,
        action: entry.action,
        entite: entry.table_name,
        entite_id: entry.record_id || null,
        details: entry.details || null,
        ip_address: null,
        user_agent: navigator.userAgent || null,
      });
    } catch (err) {
      logger.error('Erreur audit log:', err);
    }
  },

  // Helpers pour les actions CRUD de base
  async logCreation(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'creation', 
      table_name, 
      record_id: recordId, 
      details: details || { nom: table_name } 
    });
  },

  async logModification(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'modification', 
      table_name, 
      record_id: recordId, 
      details: details || { nom: table_name } 
    });
  },

  async logSuppression(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'suppression', 
      table_name, 
      record_id: recordId, 
      details: details || { nom: table_name } 
    });
  },

  // Helpers pour les consultations
  async logConsultation(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'consultation', 
      table_name, 
      record_id: recordId, 
      details: details || { reference: recordId } 
    });
  },

  // Helpers pour les téléchargements et exports
  async logTelechargement(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'telechargement', 
      table_name, 
      record_id: recordId, 
      details: details || { fichier: recordId } 
    });
  },

  async logExport(table_name: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'export', 
      table_name, 
      details: details || { type_export: table_name } 
    });
  },

  // Helpers pour les actions spécifiques aux demandes
  async logRelance(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'relance', 
      table_name, 
      record_id: recordId, 
      details: details || { reference: recordId } 
    });
  },

  async logCloture(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'cloture', 
      table_name, 
      record_id: recordId, 
      details: details || { reference: recordId } 
    });
  },

  // Helpers pour les actions spécifiques aux dossiers
  async logSoumission(dossierId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'soumission', 
      table_name: 'dossiers', 
      record_id: dossierId, 
      details: details || { dossier_id: dossierId } 
    });
  },

  async logAssignation(dossierId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'assignation', 
      table_name: 'dossiers', 
      record_id: dossierId, 
      details: details || { dossier_id: dossierId } 
    });
  },

  // Helpers pour les actions de partage
  async logPartage(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'partage', 
      table_name, 
      record_id: recordId, 
      details: details || { partage_avec: 'utilisateur' } 
    });
  },

  async logRevocation(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'revocation', 
      table_name, 
      record_id: recordId, 
      details: details || { revocation_de: recordId } 
    });
  },

  // Helpers pour les actions d'authentification
  async logDeconnexion(userId?: string): Promise<void> {
    await this.log({ 
      action: 'deconnexion', 
      table_name: 'users', 
      user_id: userId,
      details: { type: 'deconnexion_manuelle' } 
    });
  },

  async logChangementMotDePasse(userId?: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'changement_mot_de_passe', 
      table_name: 'users', 
      user_id: userId,
      details: details || { type: 'changement_volontaire' } 
    });
  },

  async logReinitialisationMotDePasse(userId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'reinitialisation_mot_de_passe', 
      table_name: 'users', 
      record_id: userId,
      details: details || { type: 'reinitialisation_admin' } 
    });
  },

  // Helpers pour les actions d'activation/désactivation
  async logActivation(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'activation', 
      table_name, 
      record_id: recordId, 
      details: details || { statut: 'actif' } 
    });
  },

  async logDesactivation(table_name: string, recordId: string, details?: Record<string, any>): Promise<void> {
    await this.log({ 
      action: 'desactivation', 
      table_name, 
      record_id: recordId, 
      details: details || { statut: 'inactif' } 
    });
  },

  // Alias pour compatibilité avec le code existant
  async logConsultationDossier(dossierId: string, details?: Record<string, any>): Promise<void> {
    await this.logConsultation('dossiers', dossierId, details);
  },
};